import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const API_BASE = '/api';

// Check if we're in production
const isProduction = () => {
  return window.location.hostname !== 'localhost' &&
         !window.location.hostname.includes('127.0.0.1');
};

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // 'loading', 'confirm', 'success', 'error', 'already'
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No se proporcionó un token de cancelación válido.');
        return;
      }

      try {
        if (isProduction()) {
          const response = await fetch(`${API_BASE}/email/unsubscribe?token=${token}`);
          const data = await response.json();

          if (data.success) {
            setEmail(data.email);
            if (data.status === 'unsubscribed') {
              setStatus('already');
              setMessage(data.message);
            } else {
              setStatus('confirm');
            }
          } else {
            setStatus('error');
            setMessage(data.message || 'Token inválido o expirado.');
          }
        } else {
          // Development mode
          setEmail('test@example.com');
          setStatus('confirm');
        }
      } catch (error) {
        console.error('Error checking token:', error);
        setStatus('error');
        setMessage('Error al verificar el token. Por favor, inténtalo de nuevo.');
      }
    };

    checkToken();
  }, [token]);

  const handleUnsubscribe = async () => {
    setIsProcessing(true);

    try {
      if (isProduction()) {
        const response = await fetch(`${API_BASE}/email/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage(data.message);
        } else {
          setStatus('error');
          setMessage(data.message || 'Error al cancelar la suscripción.');
        }
      } else {
        // Development mode
        setStatus('success');
        setMessage('¡Suscripción cancelada correctamente! (modo desarrollo)');
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setStatus('error');
      setMessage('Error al procesar la solicitud. Por favor, inténtalo de nuevo.');
    }

    setIsProcessing(false);
  };

  return (
    <>
      <Helmet>
        <title>Cancelar Suscripción | Leal Chapa y Pintura</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="unsubscribe-page">
        <div className="unsubscribe-container">
          <div className="unsubscribe-logo">
            <Link to="/">
              <img src="/assets/img/logo.svg" alt="Leal Chapa y Pintura" />
            </Link>
          </div>

          <div className="unsubscribe-card">
            {status === 'loading' && (
              <div className="unsubscribe-loading">
                <div className="spinner"></div>
                <p>Verificando...</p>
              </div>
            )}

            {status === 'confirm' && (
              <>
                <div className="unsubscribe-icon warning">
                  <i className="fas fa-envelope-open-text"></i>
                </div>
                <h1>Cancelar Suscripción</h1>
                <p>
                  ¿Estás seguro de que deseas cancelar tu suscripción al boletín de
                  Leal Chapa y Pintura?
                </p>
                {email && (
                  <p className="unsubscribe-email">
                    <strong>{email}</strong>
                  </p>
                )}
                <p className="unsubscribe-note">
                  Ya no recibirás nuestras ofertas exclusivas, promociones y novedades.
                </p>
                <div className="unsubscribe-actions">
                  <Link to="/" className="btn-secondary">
                    No, quiero seguir suscrito
                  </Link>
                  <button
                    onClick={handleUnsubscribe}
                    className="btn-primary"
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times"></i>
                        Sí, cancelar suscripción
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="unsubscribe-icon success">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h1>¡Suscripción Cancelada!</h1>
                <p>{message}</p>
                <p className="unsubscribe-note">
                  Si cambias de opinión, siempre puedes volver a suscribirte
                  desde nuestra página web.
                </p>
                <div className="unsubscribe-actions">
                  <Link to="/" className="btn-primary">
                    <i className="fas fa-home"></i>
                    Volver al Inicio
                  </Link>
                </div>
              </>
            )}

            {status === 'already' && (
              <>
                <div className="unsubscribe-icon info">
                  <i className="fas fa-info-circle"></i>
                </div>
                <h1>Ya Cancelaste tu Suscripción</h1>
                <p>{message}</p>
                <p className="unsubscribe-note">
                  No te preocupes, ya no recibirás más emails de nuestra parte.
                </p>
                <div className="unsubscribe-actions">
                  <Link to="/" className="btn-primary">
                    <i className="fas fa-home"></i>
                    Volver al Inicio
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="unsubscribe-icon error">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h1>Error</h1>
                <p>{message}</p>
                <p className="unsubscribe-note">
                  Si el problema persiste, contacta con nosotros en{' '}
                  <a href="mailto:taller@lealchapaypintura.com" target="_blank" rel="noopener noreferrer">
                    taller@lealchapaypintura.com
                  </a>
                </p>
                <div className="unsubscribe-actions">
                  <Link to="/" className="btn-primary">
                    <i className="fas fa-home"></i>
                    Volver al Inicio
                  </Link>
                </div>
              </>
            )}
          </div>

          <div className="unsubscribe-footer">
            <p>
              Leal Chapa y Pintura SL<br />
              C. Congreso, 57 | Polígono P.I.S.A<br />
              Mairena del Aljarafe, Sevilla
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .unsubscribe-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .unsubscribe-container {
          width: 100%;
          max-width: 480px;
        }

        .unsubscribe-logo {
          text-align: center;
          margin-bottom: 30px;
        }

        .unsubscribe-logo img {
          max-width: 200px;
          height: auto;
        }

        .unsubscribe-card {
          background: white;
          border-radius: 12px;
          padding: 40px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        }

        .unsubscribe-loading {
          padding: 40px 0;
        }

        .unsubscribe-loading .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #e63946;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .unsubscribe-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 36px;
        }

        .unsubscribe-icon.warning {
          background: #fff3cd;
          color: #856404;
        }

        .unsubscribe-icon.success {
          background: #d4edda;
          color: #155724;
        }

        .unsubscribe-icon.error {
          background: #f8d7da;
          color: #721c24;
        }

        .unsubscribe-icon.info {
          background: #d1ecf1;
          color: #0c5460;
        }

        .unsubscribe-card h1 {
          color: #1a1a2e;
          font-size: 24px;
          margin-bottom: 15px;
        }

        .unsubscribe-card p {
          color: #666;
          line-height: 1.6;
          margin-bottom: 10px;
        }

        .unsubscribe-email {
          background: #f8f9fa;
          padding: 10px 20px;
          border-radius: 8px;
          display: inline-block;
          color: #1a1a2e !important;
        }

        .unsubscribe-note {
          font-size: 14px;
          color: #999 !important;
          margin-top: 15px;
        }

        .unsubscribe-note a {
          color: #e63946;
        }

        .unsubscribe-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 25px;
        }

        .unsubscribe-actions button,
        .unsubscribe-actions a {
          padding: 14px 24px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .btn-primary {
          background: #e63946;
          color: white;
        }

        .btn-primary:hover {
          background: #d32f3d;
        }

        .btn-secondary {
          background: #f8f9fa;
          color: #1a1a2e;
          border: 1px solid #dee2e6;
        }

        .btn-secondary:hover {
          background: #e9ecef;
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .unsubscribe-footer {
          text-align: center;
          margin-top: 30px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 13px;
          line-height: 1.6;
        }

        @media (max-width: 480px) {
          .unsubscribe-card {
            padding: 30px 20px;
          }

          .unsubscribe-card h1 {
            font-size: 20px;
          }
        }
      `}</style>
    </>
  );
};

export default UnsubscribePage;
